import dayjs from "dayjs";

import { dateStringToDate } from "../DatepickerV2/libs/date";
import { BaseInfoIcon } from "../Icons";
import { TooltipWrapper } from "../Tooltip";
import { Typo } from "../Typo";
import {
  type ElapsedTimeTranslations,
  formatElapsedTime,
  isToday,
} from "./formatElapsedTime";

export type LastClickCellTranslations = {
  noClicksYet: string;
  lastClicked: string;
  elapsedTime: ElapsedTimeTranslations;
};

export const LastClickHeaderCell = ({
  title,
  tooltipText,
  disabled = false,
}: {
  title: string;
  tooltipText: string;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-1">
    <Typo type="bodyXS" textColor="text-text-secondary">
      {title}
    </Typo>
    <TooltipWrapper
      color="blue"
      text={tooltipText}
      disableHover={disabled}
      tooltipWidth={165}
      tooltipPlacement="top"
    >
      <BaseInfoIcon height={16} width={16} className="text-text-secondary" />
    </TooltipWrapper>
  </div>
);

// AC-1: hovering shows the exact click date (date only, no time-of-day — per
// design) as a tooltip, except when the click happened today — in that case no
// tooltip is shown at all (not an empty one).
export const ReferralLastClickDataCell = ({
  lastClickedAt,
  translations,
}: {
  lastClickedAt?: string | null;
  translations: LastClickCellTranslations;
}) => {
  const lastClickedDate = lastClickedAt
    ? dateStringToDate(lastClickedAt)
    : undefined;

  if (!lastClickedDate) {
    return (
      <Typo type="captionMD" textColor="text-text-disabled">
        {translations.noClicksYet}
      </Typo>
    );
  }

  const elapsedText = formatElapsedTime(
    lastClickedDate,
    translations.elapsedTime,
  );

  // AC-1: today's clicks render plain text — TooltipWrapper always mounts a
  // hoverable ReactTooltip once given any (even empty) text, so the only way
  // to genuinely show no tooltip is to not render the wrapper at all.
  if (isToday(lastClickedDate)) {
    return <Typo type="bodyMD">{elapsedText}</Typo>;
  }

  return (
    <TooltipWrapper
      color="blue"
      text={dayjs(lastClickedDate).format("DD MMM YYYY")}
      tooltipPlacement="top"
    >
      <Typo type="bodyMD">{elapsedText}</Typo>
    </TooltipWrapper>
  );
};

export const ReferralLastClickText = ({
  lastClickedAt,
  translations,
}: {
  lastClickedAt?: string | null;
  translations: LastClickCellTranslations;
}) => {
  const lastClickedDate = lastClickedAt
    ? dateStringToDate(lastClickedAt)
    : undefined;

  if (!lastClickedDate) {
    return (
      <Typo type="bodySM" smType="bodyXS" textColor="text-text-secondary">
        {translations.noClicksYet}
      </Typo>
    );
  }

  const elapsedText = formatElapsedTime(
    lastClickedDate,
    translations.elapsedTime,
  );

  return (
    <Typo type="bodySM" smType="bodyXS" textColor="text-text-secondary">
      {translations.lastClicked + " " + elapsedText}
    </Typo>
  );
};
